import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { QuizBank, QuizMode, bankKey } from '../models/quiz';

/** Google Analytics 4 property "drivon.am" (account "DrivOn"). */
export const GA_MEASUREMENT_ID = 'G-6KVLWNKMSR';

/** Only the live site reports, so local runs and Amplify branch previews stay out of the numbers. */
const LIVE_HOSTS = new Set(['drivon.am', 'www.drivon.am']);

export interface TestDetails {
  bank: QuizBank;
  id: number;
  mode: QuizMode;
  /** Language of the questions. */
  language: string;
}

type GtagWindow = Window & { dataLayer?: unknown[] };

/**
 * Google Analytics. Page views, including route changes, are sent by the tag itself (the
 * stream's enhanced measurement counts browser history changes); this service adds the theory
 * test events. Their parameters are registered as custom dimensions in the GA property.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private gtag?: (...args: unknown[]) => void;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  /** Loads the Google tag on the live site; does nothing anywhere else. */
  init() {
    const win = this.document.defaultView as GtagWindow | null;
    if (!win || !LIVE_HOSTS.has(win.location.hostname)) {
      return;
    }
    const dataLayer = (win.dataLayer = win.dataLayer || []);
    // gtag.js only accepts the `arguments` object, not an array, so this stays a plain function.
    this.gtag = function () {
      dataLayer.push(arguments);
    };
    this.gtag('js', new Date());
    this.gtag('config', GA_MEASUREMENT_ID);

    const script = this.document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    this.document.head.appendChild(script);
  }

  testStarted(test: TestDetails) {
    this.sendTestEvent('test_start', test);
  }

  testPassed(test: TestDetails) {
    this.sendTestEvent('test_pass', test);
  }

  private sendTestEvent(name: string, test: TestDetails) {
    this.gtag?.('event', name, {
      test_bank: bankKey(test.bank),
      test_id: test.id,
      test_mode: test.mode,
      test_language: test.language,
    });
  }
}
